"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.needsSeverityHeal = exports.shouldRaiseInactiveAlert = exports.buildSensorInactiveAlert = exports.offlineShortLine = exports.sensorTypeNoun = exports.coverageStakeLine = void 0;
const Sensor_1 = require("../types/Sensor");
const SensorAlert_1 = require("../types/SensorAlert");
/**
 * The offline-alert decision, extracted out of `scheduledStatusSweep` so it is
 * unit-testable without AWS, and so the cloud sweep and the client coverage
 * badge read their copy from ONE place and cannot drift.
 *
 * Copy rules enforced throughout this module:
 *  - the household device is always "base station", never "hub";
 *  - no sensor is ever described as built into the base station;
 *  - battery advice is given ONLY where the unit is battery powered;
 *  - nothing is framed as a hazard. A sensor going quiet is a gap in what we
 *    can see, not an emergency — wording it as an emergency is what trained
 *    homeowners to ignore the amber channel in the first place.
 */
/** Human label for a sensor, for use inside copy. */
function sensorLabel(location, name) {
    return location || name || 'sensor';
}
/**
 * What the homeowner actually loses while this sensor is silent. Shared by the
 * sweep's alert message and the client coverage badge.
 */
function coverageStakeLine(sensorType, location, name) {
    const label = sensorLabel(location, name);
    switch (sensorType) {
        case Sensor_1.SensorType.LEAK_DETECTOR:
            return `Leaks near ${label} won't be detected.`;
        case Sensor_1.SensorType.WATER_QUALITY:
            return `Water quality at ${label} isn't being checked.`;
        case Sensor_1.SensorType.AIR_QUALITY:
            return `Air quality at ${label} isn't being monitored.`;
        case Sensor_1.SensorType.HVAC_AIRHANDLER_MONITOR:
        case Sensor_1.SensorType.HVAC_CONDENSER_MONITOR:
        case Sensor_1.SensorType.HVAC_AIRHANDLER_MONITOR_CT:
        case Sensor_1.SensorType.HVAC_CONDENSER_MONITOR_CT:
            return `${label} system efficiency and filter health aren't being tracked.`;
        case Sensor_1.SensorType.POWER:
        case Sensor_1.SensorType.POWER_ALT:
        case Sensor_1.SensorType.ELECTRICAL:
            return `Power use at ${label} isn't being tracked.`;
        case Sensor_1.SensorType.HUMIDITY:
            return `Humidity at ${label} isn't being monitored.`;
        case Sensor_1.SensorType.TEMPERATURE:
            return `Temperature at ${label} isn't being monitored.`;
        case Sensor_1.SensorType.PRESSURE:
            return `Pressure at ${label} isn't being monitored.`;
        case Sensor_1.SensorType.CO2:
        case Sensor_1.SensorType.VOC:
        case Sensor_1.SensorType.PM10:
        case Sensor_1.SensorType.PM25:
            return `Air quality at ${label} isn't being monitored.`;
        default:
            // Every SensorType member must yield a real stake line — a blank one
            // renders an empty badge and an alert body with no reason in it.
            return `${label} isn't being monitored.`;
    }
}
exports.coverageStakeLine = coverageStakeLine;
/**
 * Plain noun for a sensor type, for the SHORT coverage line. Accepts the
 * aggregation cores' `'HVAC_GROUP'` pseudo-type as well as every `SensorType`
 * member — an HVAC pair is one "system" to the homeowner, never two monitors.
 */
function sensorTypeNoun(sensorType) {
    switch (sensorType) {
        case Sensor_1.SensorType.LEAK_DETECTOR:
            return 'leak detector';
        case Sensor_1.SensorType.WATER_QUALITY:
            return 'water quality sensor';
        case Sensor_1.SensorType.AIR_QUALITY:
        case Sensor_1.SensorType.CO2:
        case Sensor_1.SensorType.VOC:
        case Sensor_1.SensorType.PM10:
        case Sensor_1.SensorType.PM25:
            return 'air quality monitor';
        case 'HVAC_GROUP':
        case Sensor_1.SensorType.HVAC_AIRHANDLER_MONITOR:
        case Sensor_1.SensorType.HVAC_CONDENSER_MONITOR:
        case Sensor_1.SensorType.HVAC_AIRHANDLER_MONITOR_CT:
        case Sensor_1.SensorType.HVAC_CONDENSER_MONITOR_CT:
            return 'HVAC system';
        case Sensor_1.SensorType.POWER:
        case Sensor_1.SensorType.POWER_ALT:
        case Sensor_1.SensorType.ELECTRICAL:
            return 'power monitor';
        case Sensor_1.SensorType.HUMIDITY:
            return 'humidity sensor';
        case Sensor_1.SensorType.TEMPERATURE:
            return 'temperature sensor';
        case Sensor_1.SensorType.PRESSURE:
            return 'pressure sensor';
        case Sensor_1.SensorType.LIGHT:
            return 'light sensor';
        case Sensor_1.SensorType.MOTION:
            return 'motion sensor';
        default:
            return 'sensor';
    }
}
exports.sensorTypeNoun = sensorTypeNoun;
/**
 * The SHORT coverage line — "[location]: offline". The badge on the home cards
 * (phone and 395px hub card alike) only has to say WHICH sensor and THAT it is
 * offline; the sensor type, last-seen time and the stake sentence all live one
 * tap away in the alert card. Two earlier wordings both overflowed: the stake
 * sentence wrapped to two lines on the phone, and "[location] [sensor type] is
 * offline" ran the full width of the hub card.
 *
 * Falls back to the sensor name, then to the bare (capitalised) type noun.
 */
function offlineShortLine(sensorType, location, name) {
    const label = location || name;
    if (label)
        return `${label}: offline`;
    const noun = sensorTypeNoun(sensorType);
    return `${noun.charAt(0).toUpperCase()}${noun.slice(1)}: offline`;
}
exports.offlineShortLine = offlineShortLine;
/**
 * What to do about it. Battery advice only where there IS a battery: power
 * monitors and HVAC monitors are mains-wired, and telling their owner to check
 * batteries sends them looking for a compartment that doesn't exist.
 */
function recoveryAdvice(sensorType, location, name) {
    const label = sensorLabel(location, name);
    switch (sensorType) {
        case Sensor_1.SensorType.AIR_QUALITY:
            // Battery-only guidance, by standing product rule. Never say or imply
            // this monitor lives inside the base station.
            return `The air quality monitor (${label}) has stopped reporting. Check its batteries, and request service if it doesn't recover.`;
        case Sensor_1.SensorType.POWER:
        case Sensor_1.SensorType.POWER_ALT:
        case Sensor_1.SensorType.ELECTRICAL:
        case Sensor_1.SensorType.HVAC_AIRHANDLER_MONITOR:
        case Sensor_1.SensorType.HVAC_CONDENSER_MONITOR:
        case Sensor_1.SensorType.HVAC_AIRHANDLER_MONITOR_CT:
        case Sensor_1.SensorType.HVAC_CONDENSER_MONITOR_CT:
            return `${label} stopped reporting — check that it has power and is in range of the base station.`;
        default:
            return `${label} stopped reporting — check that it has power and is in range of the base station.`;
    }
}
function toSeconds(value) {
    if (value === undefined || value === null)
        return 0;
    const parsed = typeof value === 'number' ? value : parseInt(String(value), 10);
    return isNaN(parsed) ? 0 : parsed;
}
/**
 * The alert a silent sensor should carry.
 *
 * Severity is **Medium**, deliberately reversing `8590b37d` (2026-08-12), which
 * minted these at High to make the alert card and the dashboard card agree —
 * both red. They now agree the other way: both amber. A sensor that has gone
 * quiet is a coverage gap, and High/Critical is reserved for a hazard
 * happening now. Medium already renders amber everywhere (`mapSeverity` in
 * `useActiveAlerts`, the web-admin severity pill), the backlight ring excludes
 * offline types entirely, and notifications are gated on alertType rather than
 * severity — so nothing else moves as a result of this.
 */
function buildSensorInactiveAlert(sensor, _nowSeconds) {
    const isAirQuality = sensor.type === Sensor_1.SensorType.AIR_QUALITY;
    const offlineSince = toSeconds(sensor.lastReported);
    return {
        alertType: isAirQuality
            ? SensorAlert_1.SensorAlertType.AqSensorInactive
            : SensorAlert_1.SensorAlertType.SensorInactive,
        severity: SensorAlert_1.SensorAlertSeverity.Medium,
        message: `${coverageStakeLine(sensor.type, sensor.location, sensor.name)} ${recoveryAdvice(sensor.type, sensor.location, sensor.name)}`,
        metadata: {
            previousStatus: sensor.status,
            lastReported: sensor.lastReported,
            // When the silence actually began (vs triggeredAt = when the sweep
            // noticed, up to threshold + 15 min later).
            offlineSince: offlineSince || null,
        },
    };
}
exports.buildSensorInactiveAlert = buildSensorInactiveAlert;
/**
 * Should an already-INACTIVE row get an offline alert at all?
 *
 * No, when it never reported once. The sweep also ages never-paired PENDING
 * rows into INACTIVE, and `lastReported === 0` makes the cutoff comparison
 * trivially true — so the backfill was raising "check that it has power" for
 * sensors that were never installed, which is both wrong and contradicts the
 * sweep's own PENDING handling a few lines up.
 */
function shouldRaiseInactiveAlert(sensor, lastReportedCutoff) {
    const last = toSeconds(sensor.lastReported);
    if (last <= 0)
        return false;
    return last < lastReportedCutoff;
}
exports.shouldRaiseInactiveAlert = shouldRaiseInactiveAlert;
/**
 * Does an open offline row need its severity healed down to Medium?
 *
 * The sweep early-returns on an open episode, which is why every High row
 * already in the field would otherwise stay High forever. One conditional
 * UpdateCommand per open row per tick converges the whole fleet on the next
 * 15-minute sweep, and is a no-op once healed.
 */
function needsSeverityHeal(openAlert) {
    return openAlert.severity !== SensorAlert_1.SensorAlertSeverity.Medium;
}
exports.needsSeverityHeal = needsSeverityHeal;
